import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "../lib/animations/context";

interface ScrollRestorationProps {
  enabled?: boolean;
  smooth?: boolean;
  excludePaths?: string[];
}

const ScrollRestoration: React.FC<ScrollRestorationProps> = ({
  enabled = true,
  smooth = true,
  excludePaths = [],
}) => {
  const location = useLocation();
  const isReducedMotion = useReducedMotion();
  const isNavigating = useRef(false);
  const previousPathname = useRef(location.pathname);

  // Handle navigation state
  useEffect(() => {
    const handlePopState = () => {
      isNavigating.current = true;
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Always scroll to top on route change
  useEffect(() => {
    if (!enabled || excludePaths.includes(location.pathname)) {
      return;
    }

    // Only scroll to top if the pathname actually changed
    if (previousPathname.current !== location.pathname) {
      // Always scroll to top immediately when route changes
      if (smooth && !isReducedMotion) {
        // Smooth scroll to top
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        // Instant scroll to top
        window.scrollTo(0, 0);
      }

      // Update the previous pathname
      previousPathname.current = location.pathname;
    }

    isNavigating.current = false;
  }, [location.pathname, enabled, smooth, isReducedMotion, excludePaths]);

  return null;
};

export default ScrollRestoration;
